/**
 * api/client.ts — fetch wrapper que auto-anexa Cognito JWT ID token.
 *
 * Uso:
 *   const data = await apiFetch<EligibilityResponse>('/eligibility/me');
 *   await apiFetch('/requests', { method: 'POST', body: payload });
 *
 * Auth:
 *   - Pega ID token via fetchAuthSession() do aws-amplify/auth
 *   - API GW espera no header `Authorization` (não `Bearer ...`, é JWT direto)
 *
 * Errors:
 *   - 401 → throw ApiError, caller pode redirecionar pra /login
 *   - 4xx/5xx → throw ApiError com message do body se disponível
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown> | object | null;
  /** Skip JWT (rotas públicas). Default false. */
  noAuth?: boolean;
  signal?: AbortSignal;
}

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new ApiError('Sessão expirada. Faça login novamente.', 401);
  }
  return idToken;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { method = 'GET', body, noAuth = false, signal } = options;

  if (!API_BASE) {
    throw new ApiError('VITE_API_BASE não configurado. Verifique .env.production.', 500);
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!noAuth) {
    headers.Authorization = await getIdToken();
  }

  const url = `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    throw new ApiError(`Erro de rede: ${(err as Error).message}`, 0);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      payload = await response.json();
    } catch {
      // body inválido — segue
    }
  }

  if (!response.ok) {
    const errorPayload = (payload ?? {}) as { message?: string; details?: unknown };
    const message = errorPayload.message ?? response.statusText ?? 'Erro desconhecido';
    throw new ApiError(message, response.status, errorPayload.details);
  }

  return payload as T;
}
